import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Button,
  Box,
  Skeleton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRole } from "../../contexts/RoleContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import colors from "../../assets/theme/colors";
import { ACTIVE_CHILD_STORAGE_KEY } from "../Dashboard/shared/DashboardViewContext";
import ChildActionsMenuContent from "../Dashboard/shared/ChildActionsMenuContent";
import { PRODUCT_NAME_TITLE } from "../../constants/config";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";
import BrandWordmark from "../UI/BrandWordmark";
import TopBarShell from "../Common/TopBarShell";
import { getChildAccentTheme, getChildInitial } from "../Dashboard/shared/childAccentTheme";
import {
  clearProfileDraftDisplay,
  PROFILE_DRAFT_CLEAR_EVENT,
  PROFILE_DRAFT_DISPLAY_EVENT,
} from "../Dashboard/shared/profileDraftDisplay";
import DashboardProfileSwitchMenu from "./DashboardProfileSwitchMenu";
import UserAccountMenu from "./UserAccountMenu";

const Navbar = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileViewport = useMediaQuery("(max-width:640px)");
  const [currentUser, authLoading] = useAuthState(auth);
  const { childrenWithAccess, getUserRoleForChild } = useRole();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [dashboardChildMenuAnchor, setDashboardChildMenuAnchor] = useState(null);
  const [dashboardActionsAnchor, setDashboardActionsAnchor] = useState(null);
  const dashboardSwitcherAnchorRef = useRef(null);
  const [profileDraftDisplay, setProfileDraftDisplay] = useState(null);
  const [dashboardActiveChildId, setDashboardActiveChildId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || "";
  });
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  const isCreatingProfileRoute = location.pathname === "/children/new";
  const isInstallRoute = location.pathname.startsWith("/install");
  const isAuthRoute = ["/login", "/register", "/forgot-password"].includes(location.pathname);
  const isHomeRoute = location.pathname === "/" || location.pathname === "";
  const isHowItWorksRoute = location.pathname === "/how-it-works";
  const isInteractiveDemoRoute = location.pathname === "/interactive-demo";
  const isMarketingRoute = [
    "/",
    "/how-it-works",
    "/cookie-policy",
    "/privacy-policy",
    "/terms",
    "/terms-of-service",
    "/autism-support",
    "/elder-care",
    "/family-care",
  ].includes(location.pathname);
  const isMobileLandingHome = isHomeRoute && isMobileViewport && !isDashboardRoute && !isInstallRoute;
  const useCompactLoggedInBrand = Boolean(currentUser)
    && isMobileViewport
    && !isMarketingRoute
    && !isAuthRoute
    && !isInstallRoute;
  const dashboardActiveChild = useMemo(
    () => (
      dashboardActiveChildId
        ? childrenWithAccess.find((child) => child.id === dashboardActiveChildId) || null
        : childrenWithAccess[0] || null
    ),
    [childrenWithAccess, dashboardActiveChildId]
  );
  const newProfilePlaceholder = useMemo(() => ({
    id: "profile-draft",
    name: "New profile",
    photoURL: "",
    isDraftProfile: true,
  }), []);
  const activeProfileDraftDisplay = isCreatingProfileRoute
    ? (profileDraftDisplay || newProfilePlaceholder)
    : null;
  const dashboardDisplayChild = activeProfileDraftDisplay || dashboardActiveChild;
  const hasDashboardChildSwitcherProfile = Boolean(activeProfileDraftDisplay || childrenWithAccess.length > 0);
  const dashboardDisplayAccent = useMemo(
    () => getChildAccentTheme(dashboardDisplayChild || {}, { children: childrenWithAccess }),
    [childrenWithAccess, dashboardDisplayChild]
  );
  const userAvatarLabel =
    currentUser?.displayName?.trim()?.charAt(0)?.toUpperCase() ||
    currentUser?.email?.trim()?.charAt(0)?.toUpperCase() ||
    "U";

  useEffect(() => {
    if (!dashboardActiveChildId && childrenWithAccess[0]?.id) {
      setDashboardActiveChildId(childrenWithAccess[0].id);
    }
  }, [childrenWithAccess, dashboardActiveChildId]);

  useEffect(() => {
    const handleOpenProfileSwitcher = () => {
      if (dashboardSwitcherAnchorRef.current) {
        setDashboardChildMenuAnchor(dashboardSwitcherAnchorRef.current);
      }
    };

    window.addEventListener("captureez:open-profile-switcher", handleOpenProfileSwitcher);
    return () => {
      window.removeEventListener("captureez:open-profile-switcher", handleOpenProfileSwitcher);
    };
  }, []);

  useEffect(() => {
    const handleActiveChildChanged = (event) => {
      const nextChildId = event?.detail?.childId || window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || "";
      setDashboardActiveChildId(nextChildId);
    };

    const handleStorage = (event) => {
      if (event.key === ACTIVE_CHILD_STORAGE_KEY) {
        setDashboardActiveChildId(event.newValue || "");
      }
    };

    window.addEventListener("captureez:active-child-changed", handleActiveChildChanged);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("captureez:active-child-changed", handleActiveChildChanged);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    const handleProfileDraftDisplay = (event) => {
      if (window.location.pathname !== "/children/new") {
        setProfileDraftDisplay(null);
        return;
      }
      setProfileDraftDisplay(event?.detail || null);
    };

    const handleProfileDraftClear = () => {
      setProfileDraftDisplay(null);
    };

    window.addEventListener(PROFILE_DRAFT_DISPLAY_EVENT, handleProfileDraftDisplay);
    window.addEventListener(PROFILE_DRAFT_CLEAR_EVENT, handleProfileDraftClear);

    return () => {
      window.removeEventListener(PROFILE_DRAFT_DISPLAY_EVENT, handleProfileDraftDisplay);
      window.removeEventListener(PROFILE_DRAFT_CLEAR_EVENT, handleProfileDraftClear);
    };
  }, []);

  useEffect(() => {
    if (!isCreatingProfileRoute) {
      setProfileDraftDisplay(null);
      clearProfileDraftDisplay();
      return;
    }

    setProfileDraftDisplay(null);
  }, [isCreatingProfileRoute]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfile = () => {
    handleUserMenuClose();
    navigate("/profile");
  };

  const handleHowItWorks = () => {
    handleUserMenuClose();
    navigate("/how-it-works");
  };

  const handleLogoutClick = async () => {
    handleUserMenuClose();
    await handleLogout();
  };

  const handleDashboardChildMenuOpen = (event) => {
    setDashboardChildMenuAnchor(event.currentTarget);
  };

  const handleDashboardChildMenuClose = () => {
    setDashboardChildMenuAnchor(null);
  };

  const handleDashboardActionsClose = () => {
    setDashboardActionsAnchor(null);
  };

  const handleDashboardAction = (action) => {
    handleDashboardActionsClose();
    window.dispatchEvent(new CustomEvent("captureez:dashboard-action", {
      detail: {
        action,
        childId: dashboardActiveChild?.id || "",
      },
    }));
  };

  const handleDashboardProfileEdit = () => {
    handleDashboardChildMenuClose();
    if (activeProfileDraftDisplay?.isDraftProfile) {
      return;
    }
    const childId = dashboardActiveChild?.id || "";
    if (!childId) {
      return;
    }

    try {
      window.sessionStorage.setItem("lifelog:last-edit-child-id", childId);
    } catch (error) {
      // The route state below is the primary handoff; session storage is only a refresh fallback.
    }

    navigate(CARE_PROFILE_ROUTE, {
      state: { childId },
    });
  };

  const handleDashboardChildSelect = (childId) => {
    handleDashboardChildMenuClose();
    if (!childId) {
      return;
    }
    setProfileDraftDisplay(null);
    window.localStorage.setItem(ACTIVE_CHILD_STORAGE_KEY, childId);
    window.dispatchEvent(new CustomEvent("captureez:set-active-child", {
      detail: { childId },
    }));
    window.dispatchEvent(new CustomEvent("captureez:active-child-changed", {
      detail: { childId },
    }));

    if (isCreatingProfileRoute) {
      try {
        window.sessionStorage.setItem("lifelog:last-edit-child-id", childId);
      } catch (error) {
        // Route state carries the selected profile; storage is only a refresh fallback.
      }
      navigate(CARE_PROFILE_ROUTE, {
        state: { childId },
      });
    }
  };

  const handleDashboardAddChild = () => {
    handleDashboardChildMenuClose();
    handleDashboardActionsClose();
    setProfileDraftDisplay(null);
    try {
      window.sessionStorage.removeItem("lifelog:last-edit-child-id");
    } catch (error) {
      // The add-profile route is enough; clearing storage only prevents stale edit fallback.
    }
    navigate("/children/new");
  };

  const handleDashboardChildSwitchClick = (event) => {
    handleDashboardChildMenuOpen(event);
  };

  const renderChildAvatar = (child, {
    size = 30,
    fontSize = "0.82rem",
    accent = getChildAccentTheme(child || {}, { children: childrenWithAccess }),
    sx = {},
  } = {}) => (
    <Avatar
      src={child?.profilePhoto || child?.photoURL || child?.avatarUrl || undefined}
      sx={{
        width: size,
        height: size,
        bgcolor: accent.primary,
        color: "#fff",
        fontSize,
        fontWeight: 850,
        border: `1px solid ${alpha(accent.primary, 0.18)}`,
        ...sx,
      }}
    >
      {child ? getChildInitial(child) : "?"}
    </Avatar>
  );

  const renderLoggedOutLandingActions = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Button
        variant="text"
        component={RouterLink}
        to="/how-it-works"
        sx={{
          display: "inline-flex",
          borderRadius: "14px",
          minHeight: 34,
          px: { xs: 0.75, sm: 1.25 },
          textTransform: "none",
          fontWeight: 700,
          fontSize: { xs: "0.8rem", sm: "0.88rem" },
          color: isHowItWorksRoute ? colors.semantic.sageDeep : colors.semantic.ink,
          bgcolor: isHowItWorksRoute ? alpha(colors.semantic.sageDeep, 0.1) : "transparent",
          boxShadow: "none",
          "&:hover": {
            bgcolor: isHowItWorksRoute ? alpha(colors.semantic.sageDeep, 0.14) : "rgba(255,255,255,0.55)",
          },
        }}
      >
        How it works
      </Button>
      <Button
        variant="contained"
        component={RouterLink}
        to="/login"
        sx={{
          borderRadius: "14px",
          minHeight: 38,
          px: { xs: 1.3, sm: 1.8 },
          textTransform: "none",
          fontWeight: 700,
          fontSize: "0.88rem",
          color: "#FFFFFF",
          bgcolor: colors.semantic.sageDeep,
          boxShadow: "0 7px 18px rgba(71, 119, 101, 0.18)",
          "&:hover": {
            bgcolor: "#5C9683",
            boxShadow: "0 9px 22px rgba(71, 119, 101, 0.24)",
          },
        }}
      >
        Sign in
      </Button>
    </Box>
  );

  const renderDashboardChildSwitchAction = () => {
    if (!currentUser || !dashboardDisplayChild || !hasDashboardChildSwitcherProfile) {
      return null;
    }

    return (
      <Button
        ref={dashboardSwitcherAnchorRef}
        variant="text"
        onClick={handleDashboardChildSwitchClick}
        aria-label={`Switch profile to ${dashboardDisplayChild?.name || 'another child'}`}
        sx={{
          minWidth: 0,
          px: { xs: 0.75, sm: 0.9 },
          py: 0.4,
          borderRadius: '9999px',
          textTransform: 'none',
          bgcolor: alpha(colors.landing.surface, 0.82),
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mr: 0.5,
          '&:hover': {
            bgcolor: alpha(colors.landing.surface, 0.96),
            boxShadow: 'none',
          },
          }}
        >
        {renderChildAvatar(dashboardDisplayChild, {
          accent: dashboardDisplayAccent,
          sx: {
            width: { xs: 30, sm: 28 },
            height: { xs: 30, sm: 28 },
            fontSize: { xs: '0.82rem', sm: '0.78rem' },
            fontWeight: 800,
          },
        })}
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, alignItems: 'flex-start' }}>
          <Typography
            sx={{
              fontSize: { xs: '0.84rem', sm: '0.86rem' },
              fontWeight: 800,
              lineHeight: 1.05,
              color: colors.landing.heroText,
              maxWidth: { xs: 98, sm: 150 },
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {dashboardDisplayChild?.name || 'Switch profile'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.66rem',
              fontWeight: 750,
              lineHeight: 1,
              color: colors.landing.textMuted,
              whiteSpace: 'nowrap',
            }}
          >
            {isMobileViewport ? 'Caring for' : 'Switch profile'}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: colors.landing.textMuted, flexShrink: 0 }} />
      </Button>
    );
  };

  const renderLoggedInDashboardActions = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {renderDashboardChildSwitchAction()}
      {renderAccountMenuButton()}
    </Box>
  );

  const renderAccountMenuButton = ({
    size = 32,
    fontSize = "0.88rem",
    sx = {},
  } = {}) => (
    <Button
      onClick={handleUserMenuOpen}
      aria-label="Profile options"
      sx={{
        minWidth: 0,
        p: 0,
        ml: 0.5,
        borderRadius: "9999px",
        bgcolor: "transparent",
        boxShadow: "none",
        "&:hover": {
          bgcolor: "transparent",
          boxShadow: "none",
        },
        ...sx,
      }}
    >
      <Avatar
        alt={currentUser?.displayName || "User"}
        src={currentUser?.photoURL || undefined}
        sx={{
          width: size,
          height: size,
          bgcolor: colors.landing.cyanPop,
          color: colors.landing.deepNavy,
          fontSize,
          fontWeight: 850,
          border: `1px solid ${colors.landing.borderMedium}`,
          boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
        }}
      >
        {userAvatarLabel}
      </Avatar>
    </Button>
  );

  const renderLoggedInLandingActions = () => (
    <>
      {!isMobileViewport ? (
        <Button
          variant="text"
          component={RouterLink}
          to="/how-it-works"
          sx={{
            display: "inline-flex",
            borderRadius: "14px",
            minHeight: 34,
            px: 1.25,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.88rem",
            color: isHowItWorksRoute ? colors.semantic.sageDeep : colors.semantic.ink,
            bgcolor: isHowItWorksRoute ? alpha(colors.semantic.sageDeep, 0.1) : "transparent",
            boxShadow: "none",
            "&:hover": {
              bgcolor: isHowItWorksRoute ? alpha(colors.semantic.sageDeep, 0.14) : "rgba(255,255,255,0.55)",
            },
          }}
        >
          How it works
        </Button>
      ) : null}
      <Button
        variant="contained"
        component={RouterLink}
        to="/home"
        sx={{
          minHeight: 38,
          px: { xs: 1.3, sm: 1.8 },
          borderRadius: "14px",
          color: "#FFFFFF",
          bgcolor: colors.semantic.sageDeep,
          boxShadow: "0 7px 18px rgba(71, 119, 101, 0.18)",
          textTransform: "none",
          fontWeight: 700,
          fontSize: { xs: "0.8rem", sm: "0.88rem" },
          whiteSpace: "nowrap",
          "&:hover": {
            bgcolor: "#5C9683",
            boxShadow: "0 9px 22px rgba(71, 119, 101, 0.24)",
          },
        }}
      >
        Go to Home
      </Button>
      {renderAccountMenuButton()}
    </>
  );

  const renderMobileAppHeader = () => (
    <TopBarShell
      appBarSx={{
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: colors.landing.surface,
        color: colors.landing.heroText,
      }}
      toolbarSx={{
        minHeight: "calc(72px + env(safe-area-inset-top))",
        height: "calc(72px + env(safe-area-inset-top))",
        px: { xs: 1.3, sm: 1.75 },
      }}
      showShadow={false}
    >
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "44px minmax(0, 1fr) 44px 48px",
          alignItems: "center",
          gap: 1.15,
        }}
      >
        <Button
          component={RouterLink}
          to="/"
          aria-label={`${PRODUCT_NAME_TITLE} home`}
          sx={{
            minWidth: 0,
            width: 44,
            height: 44,
            p: 0,
            borderRadius: "14px",
            bgcolor: colors.semantic.profileFlow.cardBg,
            boxShadow: "none",
            "&:hover": {
              bgcolor: colors.semantic.profileFlow.cardBg,
              boxShadow: "none",
            },
          }}
        >
          <BrandWordmark
            variant="navbar"
            showText={false}
            sx={{ gap: 0 }}
          />
        </Button>

        <Button
          ref={dashboardSwitcherAnchorRef}
          variant="text"
          onClick={handleDashboardChildSwitchClick}
          aria-label={`Switch profile from ${dashboardDisplayChild?.name || "current profile"}`}
          sx={{
            minWidth: 0,
            width: "100%",
            p: 0,
            justifyContent: "flex-start",
            textTransform: "none",
            color: colors.landing.heroText,
            bgcolor: "transparent",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          {renderChildAvatar(dashboardDisplayChild, {
            accent: dashboardDisplayAccent,
            size: 52,
            fontSize: "1.05rem",
            sx: {
              width: 52,
              height: 52,
              mr: 1.05,
              flexShrink: 0,
            },
          })}
          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 850,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: colors.landing.textMuted,
                lineHeight: 1,
              }}
            >
              Caring for
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: "1.25rem",
                fontWeight: 900,
                lineHeight: 1,
                color: colors.landing.heroText,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {dashboardDisplayChild?.name || "Profile"}
            </Typography>
          </Box>
        </Button>

        <Button
          ref={dashboardSwitcherAnchorRef}
          onClick={handleDashboardChildSwitchClick}
          aria-label="Open profile switcher"
          sx={{
            minWidth: 0,
            width: 44,
            height: 44,
            p: 0,
            borderRadius: "50%",
            bgcolor: colors.landing.surfaceSoft,
            color: colors.landing.textMuted,
            boxShadow: "none",
            "&:hover": {
              bgcolor: colors.landing.sageLight,
              boxShadow: "none",
            },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 24 }} />
        </Button>

        {renderAccountMenuButton({
          size: 48,
          fontSize: "1rem",
          sx: { ml: 0 },
        })}
      </Box>
    </TopBarShell>
  );

  const headerContent = useCompactLoggedInBrand ? renderMobileAppHeader() : isMobileLandingHome ? (
    <TopBarShell
      left={
        <Button
          component={RouterLink}
          to="/"
          aria-label={`${PRODUCT_NAME_TITLE} home`}
          sx={{
            minWidth: "auto",
            px: 0,
            py: 0,
            justifySelf: "start",
            textTransform: "none",
            bgcolor: "transparent",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <BrandWordmark
            variant="navbar"
            showText={!useCompactLoggedInBrand}
            sx={useCompactLoggedInBrand ? { gap: 0 } : undefined}
          />
        </Button>
      }
      right={
        authLoading ? null : currentUser ? renderLoggedInLandingActions() : renderLoggedOutLandingActions()
      }
    />
  ) : (
    <TopBarShell
      appBarSx={{
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: colors.landing.surface,
        color: colors.landing.heroText,
      }}
      toolbarSx={{
        minHeight: "calc(52px + env(safe-area-inset-top))",
        height: "calc(52px + env(safe-area-inset-top))",
        px: { xs: 2.5, sm: 2, md: 3 },
      }}
      left={
        <Button
          component={RouterLink}
          to="/"
          aria-label={`${PRODUCT_NAME_TITLE} home`}
          sx={{
            minWidth: "auto",
            px: 0,
            py: 0,
            justifySelf: "start",
            textTransform: "none",
            bgcolor: "transparent",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <BrandWordmark
            variant="navbar"
            showText={!useCompactLoggedInBrand}
            sx={useCompactLoggedInBrand ? { gap: 0 } : undefined}
          />
        </Button>
      }
      right={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {authLoading ? null : currentUser ? (isDashboardRoute ? renderLoggedInDashboardActions() : renderLoggedInLandingActions()) : isDashboardRoute ? (
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              sx={{ bgcolor: colors.navbar.loadingSkeletonBg }}
            />
          ) : (
            renderLoggedOutLandingActions()
          )}
        </Box>
      }
    />
  );

  if (isInteractiveDemoRoute) return null;

  return (
    <>
      {headerContent}
      <UserAccountMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onProfile={handleProfile}
        onHowItWorks={handleHowItWorks}
        onLogout={handleLogoutClick}
      />

      {hasDashboardChildSwitcherProfile ? (
        <DashboardProfileSwitchMenu
          anchorEl={dashboardChildMenuAnchor}
          open={Boolean(dashboardChildMenuAnchor)}
          onClose={handleDashboardChildMenuClose}
          activeProfileDraftDisplay={activeProfileDraftDisplay}
          dashboardActiveChild={dashboardActiveChild}
          dashboardDisplayChild={dashboardDisplayChild}
          dashboardDisplayAccent={dashboardDisplayAccent}
          childrenWithAccess={childrenWithAccess}
          onEditProfile={handleDashboardProfileEdit}
          onSelectChild={handleDashboardChildSelect}
          onAddChild={handleDashboardAddChild}
          renderChildAvatar={renderChildAvatar}
        />
      ) : null}

      <Menu
        anchorEl={dashboardActionsAnchor}
        open={Boolean(dashboardActionsAnchor)}
        onClose={handleDashboardActionsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: '18px',
            border: `1px solid ${colors.landing.borderLight}`,
            boxShadow: `0 24px 60px ${colors.landing.shadowPanel}`,
            bgcolor: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(16px)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 0.5 }}>
          <ChildActionsMenuContent
            child={dashboardActiveChild}
            userRole={getUserRoleForChild?.(dashboardActiveChild?.id)}
            careTeamCount={Array.isArray(dashboardActiveChild?.users?.members) ? dashboardActiveChild.users.members.length : 0}
            onAddChild={handleDashboardAddChild}
            onGoToCareTeam={() => handleDashboardAction('view-care-team')}
            onEditChild={() => handleDashboardAction('edit-child')}
            onInviteTeamMember={() => handleDashboardAction('invite-caregiver')}
            onDeleteChild={() => handleDashboardAction('delete-child')}
            onPrepForTherapy={() => handleDashboardAction('prep-for-therapy')}
            onImportLogs={() => handleDashboardAction('import-logs')}
          />
        </Box>
        <Box sx={{ px: 1.5, pb: 0.75, pt: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.landing.textMuted }}>
            Account
          </Typography>
        </Box>
        <MenuItem onClick={handleProfile} sx={{ gap: 1.1, py: 1.25, px: 1.5, minHeight: 48 }}>
          <ListItemIcon sx={{ minWidth: 34 }}>
            <PersonOutlineIcon sx={{ fontSize: 18, color: colors.brand.ink }} />
          </ListItemIcon>
          My account
        </MenuItem>
        <MenuItem onClick={handleLogoutClick} sx={{ gap: 1.1, py: 1.25, px: 1.5, minHeight: 48 }}>
          <ListItemIcon sx={{ minWidth: 34 }}>
            <LogoutIcon sx={{ fontSize: 18, color: colors.landing.textMuted }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Navbar;
